import React, { Component } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { getFunnel, SEGMENTS, SESSION_EXPIRED } from '../services/funnelService';
import PeriodSelector from './PeriodSelector';
import FunnelTimeseries from './FunnelTimeseries';
import { rowsToCSV, downloadCSV } from '../lib/csv';

// Libellés FR affichés ; les CLÉS machine lues dans la réponse sont, depuis le contrat C4 **v2**
// (B6_01 §9), {installed, inscription, activation, retention, souscription, paying} — six étapes,
// dans cet ordre. ⛔ `conversion` (v1) n'existe plus : elle a été SCINDÉE en `souscription`
// (a un plan) et `paying` (paie réellement), parce que confondre les deux masquait le seul
// chiffre que le tableau de bord n'avait jamais donné.
const STEP_LABELS = {
  installed: 'Installé (plugin)',
  inscription: 'Inscription',
  activation: 'Activation',
  retention: 'Rétention',
  souscription: 'Souscription',
  paying: 'Payant actif'
};

// COMMENT chaque étape est mesurée. Deux étapes de natures différentes ne se comparent pas :
// le « décrochage » entre elles n'est pas une fuite, c'est un changement d'instrument. Le code
// le disait déjà en commentaire ; le libellé rouge, lui, ne le disait pas — c'est le défaut 2
// que ce ticket corrige.
const STEP_NATURES = {
  installed: 'signal du plugin',
  inscription: 'création de compte',
  activation: 'trafic widget',
  retention: 'trafic widget',
  souscription: 'facturation',
  paying: 'facturation'
};

// Pourquoi une étape n'est pas mesurable, tel que servi par l'API (`steps[].reason`).
// Repli sur la clé brute : si l'API ajoute un motif avant l'UI, on l'affiche plutôt que rien.
const STEP_REASON_LABELS = {
  channel_not_open: 'canal de mesure pas encore ouvert',
  data_expired: 'données purgées (au-delà de la rétention)',
  not_deployed: 'mesure pas encore déployée'
};

// Périmètre des compteurs de parc, SERVI par l'API (`triggers.scope`). Le contrat §9.9 le
// justifie mot pour mot : « servi dans la réponse pour que l'écran puisse le dire ; sans lui,
// un compteur de parc posé au milieu d'un écran de cohorte se lirait comme un chiffre de la
// période ». ⛔ Ne pas le coder en dur — c'est ce qui ment le jour où le périmètre change.
const TRIGGER_SCOPE_LABELS = {
  all_sites: 'tout le parc',
  cohort: 'communautés de la période'
};

// D24 — DEUX segments, plus « Tous ». ⛔ Pas de « Hors WordPress » : rien en base ne distingue
// un site créé au back-office d'un plugin installé qui ne s'est jamais connecté. Le libellé
// d'`unknown` porte donc la césure explicite — sans elle, un lecteur comprend « hors WordPress »,
// exactement le contresens que cette valeur existe pour éviter.
const SEGMENT_OPTIONS = [
  { value: 'all', label: 'Tous', help: 'Toute la cohorte de la période, sans distinction d’origine.' },
  {
    value: 'wp',
    label: 'WordPress',
    help: 'Communautés dont l’origine WordPress est établie : le plugin s’est connecté au moins une fois, ou l’import déclare la plateforme.'
  },
  {
    value: 'unknown',
    label: 'Origine inconnue',
    help:
      'Communautés dont rien n’indique l’origine WordPress — pour l’essentiel celles créées avant l’ouverture du canal plugin, le 10/07/2026. ⛔ Ce n’est PAS « hors WordPress » : un plugin installé sans jamais s’être connecté tombe ici aussi.'
  }
];

// D24 exige que la césure soit portée par le LIBELLÉ, pas cachée dans une infobulle : au
// survol elle est invisible au tactile, et le contresens qu'elle existe pour éviter (« hors
// WordPress ») se lit, lui, en permanence. Le texte complet ne tient pas sur un bouton de
// barre → il s'affiche en clair sous le tunnel dès que ce segment est actif.
export const SEGMENT_UNKNOWN_NOTE =
  '« Origine inconnue » = communautés dont rien n’indique l’origine WordPress, pour l’essentiel celles créées avant l’ouverture du canal plugin, le 10/07/2026. Ce n’est pas « hors WordPress » : un plugin installé sans jamais s’être connecté tombe ici aussi.';

// Paliers de rétention servis dans `retention[]` (contrat C4 v2 §9.6). ⛔ Pas de J+90 : la v2
// n'en sert que deux. Le repli `|| tier` couvre l'ajout d'un palier côté API avant l'UI.
const TIER_LABELS = { d7: 'J+7', d30: 'J+30' };

// `deactivations.scope` NOMME la population du dénominateur (D26). ⚠ Ne vaut plus
// `connected_sites_only`, et `estimated_partial` n'existe plus : ces deux champs de la v1 ont
// été retirés par B6_03 — ne pas coder contre eux.
const DEACTIVATION_SCOPE_LABELS = {
  sites_with_plugin_signal: 'sites ayant émis un signal du plugin'
};

// Réglages hérités remontés par l'endpoint. ⚠ Ce ne sont PLUS des « déclencheurs de
// conversion » (D13) : le déblocage IA n'a plus d'interrupteur dans l'interface ni d'effet
// sur la modération, son compteur ne peut que stagner ou décroître. L'annoncer comme un
// motif d'achat reviendrait à remplacer un zéro faux par un signal faux — pire, parce que
// le zéro n'induisait personne en erreur.
const TRIGGER_LABELS = {
  quota: 'Quota dépassé',
  legacy_ai_unlock: 'sites portant l’ancien déblocage IA'
};

// Les trois états d'un montant (contrat C4 v2 §9.8). « 0,00 € » ne doit plus JAMAIS
// s'afficher pour un montant qui n'est pas mesuré : c'est le défaut que ce ticket corrige.
const LIBELLES_ETAT_MRR = {
  devis: 'Sur devis',
  non_calculable: 'Montant non calculable',
  aucune_vente: 'Aucune vente',
  // ⛔ État distinct du zéro : le PÉRIMÈTRE n'est pas servi (API plus ancienne que l'écran —
  // scénario réel si le super-admin est déployé avant l'API du même jalon). Répondre
  // « Aucune vente » à une absence de donnée, c'est le défaut que B5_01 vient de corriger.
  absent: 'Non mesuré'
};

// Ce qui s'affiche à la place d'un chiffre quand la mesure n'existe pas. UNE constante, pour
// que la règle « jamais 0 quand ce n'est pas mesuré » (D21) soit vérifiable d'un seul endroit.
export const NON_MESURE = 'non mesuré';

// Un montant dans sa devise. Repli si le code ISO servi est invalide (Intl lève).
export function formatMontant(amount, currency) {
  const code = String(currency || '').toUpperCase();
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: code }).format(amount);
  } catch (e) {
    return `${new Intl.NumberFormat('fr-FR').format(amount)} ${code}`.trim();
  }
}

// Décrit UN périmètre de montant (`cohort` ou `platform`) en trois états :
//   • calculé        → un montant PAR DEVISE. On ne les additionne jamais : la base porte
//                      les mêmes offres en euros ET en dollars, et il n'existe aucun taux
//                      de change ici.
//   • sur devis      → des comptes Enterprise dont le tarif est contractuel, absent de la
//                      base. Ce n'est pas zéro.
//   • non calculable → le plan porté n'a aucun prix connu.
//   • aucune vente   → il n'y a réellement personne à compter.
// Fonction PURE et exportée : la règle d'honnêteté se verrouille par un test unitaire,
// sans monter le composant ni ajouter d'outil de rendu (patron D30).
export function describeMrr(perimetre) {
  if (perimetre === null || perimetre === undefined) {
    return { etat: 'absent', montants: [], mention: null };
  }
  const p = perimetre || {};
  const parDevise = Array.isArray(p.by_currency) ? p.by_currency : [];
  const quoted = Number(p.quoted) || 0;
  const unjoinable = Number(p.unjoinable) || 0;

  const montants = parDevise
    // ⚠ `Number(null)` vaut 0 et passe `Number.isFinite` : sans le test d'absence, un
    // montant manquant s'afficherait « 0,00 € » — exactement ce qu'on interdit ici.
    .filter((e) => e && e.amount !== null && e.amount !== undefined)
    .filter((e) => Number.isFinite(Number(e.amount)))
    .map((e) => formatMontant(Number(e.amount), e.currency));

  let etat = 'calcule';
  if (!montants.length) {
    if (quoted > 0) etat = 'devis';
    else if (unjoinable > 0) etat = 'non_calculable';
    else etat = 'aucune_vente';
  }

  // Les compteurs restent visibles MÊME quand un montant est calculé : sans eux, un
  // montant partiel se lirait comme un total.
  const mentions = [];
  if (quoted > 0) mentions.push(`${quoted} sur devis`);
  if (unjoinable > 0) mentions.push(`${unjoinable} sans tarif connu`);

  return { etat, montants, mention: mentions.join(' · ') || null };
}

// ── Règles d'honnêteté, en fonctions PURES exportées (D30) ────────────────────────────────
// Aucun outil de test de rendu dans ce repo (règle #10 : on n'en ajoute pas). Les garanties
// se verrouillent donc ici, comme `blocsAffiches.js` le fait pour sa taxonomie.

// Volume servi par l'API. ⛔ `null` = NON MESURÉ, et se distingue d'un vrai `0` : le
// `Number(x) || 0` de la v1 écrasait les deux dans la même valeur, ce qui faisait lire
// « rien ne s'est passé » là où il fallait lire « on ne sait pas encore ».
export function stepVolume(step) {
  const s = step || {};
  if (s.volume === null || s.volume === undefined) return null;
  const n = Number(s.volume);
  return Number.isFinite(n) ? n : null;
}

// Une étape est mesurable si l'API ne dit pas le contraire ET si son volume existe.
export function stepMeasurable(step) {
  const s = step || {};
  if (s.measurable === false) return false;
  return stepVolume(s) !== null;
}

// Installations RELIÉES à un site (`steps[].connected`, servi sur `installed` seulement :
// jonction balise d'installation → site, `funnel_controller` L19). `null` = l'API ne le sert
// pas (étape non mesurée, ou autre étape) : on n'affiche rien, jamais « 0 reliées ».
export function stepConnected(step) {
  const s = step || {};
  if (s.connected === null || s.connected === undefined) return null;
  const n = Number(s.connected);
  return Number.isFinite(n) ? n : null;
}

// Libellé unique écran/CSV (🔵3 revue : « 1 reliées »). Zéro et un au singulier.
export function connectedLabel(n) {
  return `${n} reliée${n > 1 ? 's' : ''} à un site`;
}

// Pourquoi une étape n'est pas mesurée, en clair. Repli sur la clé brute : un motif ajouté
// côté API avant l'UI s'affiche tel quel plutôt que de disparaître.
export function stepReason(step) {
  const s = step || {};
  if (!s.reason) return null;
  return STEP_REASON_LABELS[s.reason] || s.reason;
}

// Un taux (`steps[].rate`, `retention[].rate`) : `null` → PAS de pourcentage, jamais « 0,0 % ».
// C'est la règle D21, et c'est le seul endroit qui formate un taux dans cet écran — passages
// de barre à barre COMPRIS (🔵6 de la revue : un `toFixed` en dur ailleurs rendait ce
// commentaire faux, et aurait fait diverger deux formatages du même objet).
export function formatRate(rate) {
  if (rate === null || rate === undefined) return null;
  const n = Number(rate);
  if (!Number.isFinite(n)) return null;
  return `${(n * 100).toFixed(1)} %`;
}

// Taux de passage étape→étape (volume[i] / volume[i-1]), pour repérer « où ça fuit ».
// ⚠ Le funnel n'est PAS monotone : les étapes ne sont pas mesurées de la même façon
// (`activation`/`retention` viennent du trafic, `souscription`/`paying` de l'état de
// facturation à l'instant t) → l'étape amont peut être à 0 alors que l'aval est > 0.
// Un passage est INDÉTERMINÉ (→ `null`, affiché « n/a ») dans trois cas, jamais 0 :
//   • l'une des deux étapes n'est pas mesurée (`installed` tant que le canal est fermé) ;
//   • l'étape amont vaut 0 (division par zéro) ;
//   • la première étape, qui n'a pas d'amont.
export function computePassages(steps) {
  const list = Array.isArray(steps) ? steps : [];
  return list.map((s, i) => {
    if (i === 0) return null;
    const prev = stepVolume(list[i - 1]);
    const cur = stepVolume(s);
    if (prev === null || cur === null) return null;
    return prev > 0 ? cur / prev : null;
  });
}

// Le plus gros décrochage — et s'il oppose deux étapes de MÊME nature. Quand les natures
// diffèrent, l'écart reste signalé mais n'est plus accusé d'être une fuite : c'est ce que
// le ticket appelle « nuancer le libellé ».
export function worstPassage(steps, passages) {
  const list = Array.isArray(steps) ? steps : [];
  const ps = Array.isArray(passages) ? passages : [];
  let index = -1;
  let value = Infinity;
  ps.forEach((p, i) => {
    if (p !== null && p < value) {
      value = p;
      index = i;
    }
  });
  if (index <= 0) return { index: -1, sameNature: true, from: null, to: null };
  const from = STEP_NATURES[(list[index - 1] || {}).step] || null;
  const to = STEP_NATURES[(list[index] || {}).step] || null;
  return { index, sameNature: Boolean(from) && from === to, from, to };
}

// Un palier de rétention. ⛔ Jamais de palier affiché sans son dénominateur, et jamais
// « 0 % » quand personne n'est assez âgé pour être évalué : l'API sert alors `rate: null`
// avec `denominator: 0` — l'écran n'a qu'à ne pas le retraduire en zéro.
export function describeTier(entry) {
  const t = entry || {};
  const tier = t.tier || null;
  const label = TIER_LABELS[tier] || tier || '—';
  const denominator = Number(t.denominator);
  const hasDenominator = Number.isFinite(denominator) && denominator > 0;
  if (!hasDenominator) {
    return {
      tier,
      label,
      measurable: false,
      denominator: 0,
      volume: null,
      rateText: null,
      text: 'pas encore mesurable sur cette période'
    };
  }
  const volume = Number(t.volume) || 0;
  const rateText = formatRate(t.rate);
  return {
    tier,
    label,
    measurable: true,
    denominator,
    volume,
    rateText,
    text: `${volume} / ${denominator} sites mesurables`
  };
}

// Taille de la cohorte de la période. ⛔ NE JAMAIS la lire dans `steps[0]` : depuis D31 la
// première étape est `installed`, non mesurée (`volume: null`) — la garde d'état vide de la
// v1 concluait « Aucune donnée sur la période » alors que la cohorte existait. `cohort.size`
// est servi précisément pour ça ; le repli va chercher `inscription` PAR SA CLÉ, jamais par
// sa position.
export function cohortSize(data) {
  const d = data || {};
  const c = d.cohort || {};
  if (c.size !== null && c.size !== undefined) {
    const n = Number(c.size);
    if (Number.isFinite(n)) return n;
  }
  const steps = Array.isArray(d.steps) ? d.steps : [];
  return stepVolume(steps.find((s) => s && s.step === 'inscription')) || 0;
}

// Carte « Désinstallations ». Le dénominateur est SERVI (D26) : il n'existait nulle part
// avant B6_03. ⛔ Ne jamais rapporter ces désinstallations aux activations — « 19 sur 174 »
// divise deux populations différentes (174 = sites avec du trafic widget, pas des sites
// reliés au plugin), et c'est faux.
// ⚠ L17 (B8_02) : ce compteur n'est PAS borné à la période affichée — `Event.distinct` sans
// filtre de date côté API — alors que la courbe « Séries quotidiennes » l'est. Les deux chiffres
// coexistent à l'écran, chacun avec son libellé (« depuis l'ouverture du suivi » ici, « sur la
// période » sur la courbe) ; `sum(series.deactivations) ≤ volume` est structurel, pas un bug.
export function describeDeactivations(deactivations) {
  if (deactivations === null || deactivations === undefined) {
    return { absent: true, volume: null, denominator: 0, population: null, rateText: null, text: NON_MESURE };
  }
  const d = deactivations || {};
  const volume = Number(d.volume) || 0;
  const denominator = Number(d.denominator);
  const scope = d.scope || null;
  const population = DEACTIVATION_SCOPE_LABELS[scope] || scope || 'sites du périmètre';
  const hasDenominator = Number.isFinite(denominator) && denominator > 0;
  if (!hasDenominator) {
    return {
      absent: false,
      volume,
      denominator: 0,
      population,
      rateText: null,
      // ⛔ Pas de mention temporelle ici — et surtout pas « sur cette période » : ce compteur
      // n'est pas borné dans le temps, et « sur la période » est le libellé de la COURBE (L17).
      text: `aucun ${population.replace(/^sites /, 'site ')} — pas de dénominateur`
    };
  }
  return {
    absent: false,
    volume,
    denominator,
    population,
    rateText: formatRate(volume / denominator),
    text: `sur ${denominator} ${population} — depuis l'ouverture du suivi`
  };
}

// Seconde lecture de la conversion (D19) : ce qui a été ACHETÉ pendant la période, reconstruit
// depuis les factures payées. C'est le seul chiffre STABLE de l'écran — `current` s'efface au
// départ d'un client (le plan repasse à `null`), `invoiced` survit.
// ⛔ `revenue` est un objet PAR DEVISE, éventuellement VIDE : ne jamais lire `revenue.eur` en
// direct, itérer sur les clés présentes. Aucune vente ne fabrique pas une devise.
export function describeInvoiced(invoiced) {
  if (invoiced === null || invoiced === undefined) {
    return { absent: true, sites: null, montants: [], hasSale: false };
  }
  const inv = invoiced || {};
  const sites = Number(inv.sites) || 0;
  const revenue = inv.revenue && typeof inv.revenue === 'object' ? inv.revenue : {};
  const montants = Object.keys(revenue)
    .filter((c) => revenue[c] !== null && revenue[c] !== undefined)
    .filter((c) => Number.isFinite(Number(revenue[c])))
    .map((c) => formatMontant(Number(revenue[c]), c));
  return { absent: false, sites, montants, hasSale: sites > 0 || montants.length > 0 };
}

// ⛔ Ce qui ÉTIQUETTE les chiffres (période, segment) vient de l'écho SERVI, jamais du state
// local. Deux raisons mesurées, pas théoriques :
//   • `handlePeriodChange` met à jour les champs de date SANS recharger (« le rechargement
//     attend le Rafraîchir ») → modifier « Du » puis exporter produirait un fichier qui
//     annonce une période que ses lignes ne couvrent pas. Une fois le CSV parti par mail,
//     plus personne ne peut s'en apercevoir.
//   • une borne vide n'est pas transmise → le serveur applique sa fenêtre de 90 j, et seul
//     son écho dit laquelle.
// D37 a été livré pour ça (`funnel_controller.js`) : « l'écho porte le jour CALCULÉ, au même
// format — c'est le seul comportement qui rende la réponse auto-descriptive ». `segment` est
// de même l'écho de la valeur EFFECTIVE (une valeur inconnue est normalisée en `all`).
// Le repli sur le state ne sert QUE si l'écho manque (API antérieure à D37).
export function servedLabels(data, state) {
  const d = data || {};
  const st = state || {};
  const estJour = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
  return {
    from: estJour(d.from) ? d.from : st.from,
    to: estJour(d.to) ? d.to : st.to,
    segment: SEGMENTS.includes(d.segment) ? d.segment : st.segment
  };
}

// Export CSV — fonction PURE, testée. L'export est fidèle à l'AFFICHÉ (convention B4_01) :
// un taux non mesuré y sort « non mesuré », jamais « 0,0 ». Sections séparées par une ligne
// vide (`[]`), comme l'export des feedbacks.
export function buildCsvRows(data, from, to, segment) {
  const d = data || {};
  const steps = Array.isArray(d.steps) ? d.steps : [];
  const passages = computePassages(steps);
  const pct = (rate) => {
    const text = formatRate(rate);
    return text === null ? NON_MESURE : text.replace(' %', '').replace('.', ',');
  };
  const segLabel = (SEGMENT_OPTIONS.find((o) => o.value === segment) || {}).label || segment;

  const rows = [
    ['Période', from || '', to || ''],
    ['Segment', segLabel || ''],
    ['Cohorte (communautés créées sur la période)', String(cohortSize(d))],
    []
  ];

  rows.push(['Étape', 'Volume', 'Taux (%)', 'Passage (%)', 'Mesure']);
  steps.forEach((s, i) => {
    const volume = stepVolume(s);
    const passage = passages[i];
    // 🔵3 — l'export se revendique « fidèle à l'AFFICHÉ » : le motif de non-mesure, que
    // l'écran montre, doit donc y être aussi. Sans lui, une étape vide dans le tableur ne
    // dit pas POURQUOI elle est vide.
    const reason = stepReason(s);
    const connected = stepConnected(s);
    rows.push([
      STEP_LABELS[s.step] || s.step,
      volume === null ? NON_MESURE : String(Math.round(volume)),
      pct(s.rate),
      i === 0 ? '' : passage === null ? 'n/a' : pct(passage),
      [
        STEP_NATURES[s.step] || '',
        volume === null && reason ? `non mesurée : ${reason}` : '',
        // Même condition que l'écran (l'export se revendique fidèle à l'AFFICHÉ) : une étape
        // non mesurée ne porte jamais de « reliées », même si l'API servait `connected`.
        volume !== null && connected !== null ? connectedLabel(connected) : ''
      ]
        .filter(Boolean)
        .join(' — ')
    ]);
  });

  const tiers = Array.isArray(d.retention) ? d.retention : [];
  if (tiers.length) {
    rows.push([]);
    rows.push(['Palier de rétention', 'Sites retenus', 'Cohorte mesurable (dénominateur)', 'Taux (%)']);
    tiers.forEach((t) => {
      const tier = describeTier(t);
      rows.push([
        tier.label,
        tier.measurable ? String(tier.volume) : NON_MESURE,
        tier.measurable ? String(tier.denominator) : '0',
        tier.measurable ? pct(t.rate) : NON_MESURE
      ]);
    });
  }

  const invoiced = describeInvoiced((d.conversions || {}).invoiced);
  rows.push([]);
  rows.push(['Conversion — seconde lecture (factures payées)', 'Sites', 'Chiffre d’affaires']);
  rows.push([
    'Facturé sur la période',
    invoiced.absent ? NON_MESURE : String(invoiced.sites),
    invoiced.absent ? NON_MESURE : invoiced.montants.length ? invoiced.montants.join(' + ') : 'aucune vente'
  ]);

  const deact = describeDeactivations(d.deactivations);
  rows.push([]);
  // L17 — fidèle à l'AFFICHÉ (convention B4_01) : l'en-tête de carte porte « depuis l'ouverture
  // du suivi », l'export le recopie. La courbe, elle, compte « sur la période ».
  rows.push(["Désinstallations — depuis l'ouverture du suivi", 'Volume', 'Dénominateur', 'Périmètre du dénominateur']);
  rows.push([
    '',
    deact.absent ? NON_MESURE : String(deact.volume),
    // 🔵8 — `0` est ici la valeur SERVIE (aucun site capable d'émettre un signal), pas un
    // trou : un tableur qui l'additionne compte juste. Seule l'ABSENCE du bloc sort en
    // « non mesuré ».
    deact.absent ? NON_MESURE : deact.denominator > 0 ? String(deact.denominator) : '0',
    deact.population || NON_MESURE
  ]);

  // Les cellules chiffrées restent BRUTES (pas de séparateur de milliers) pour rester
  // exploitables dans un tableur.
  return rows;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WINDOW_DAYS = 90; // = défaut de l'endpoint

// YYYY-MM-DD : valeur d'un <input type="date"> ET borne envoyée à l'endpoint.
function toInputDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

class Funnel extends Component {
  constructor(props) {
    super(props);
    const to = new Date();
    const from = new Date(to.getTime() - DEFAULT_WINDOW_DAYS * DAY_MS);
    this.state = {
      from: toInputDate(from),
      to: toInputDate(to),
      segment: 'all',
      data: null,
      isLoading: false,
      error: null,
      errorCode: null,
      // Incrémenté à CHAQUE fetch de l'agrégat : la courbe (FunnelTimeseries) recharge sur ce
      // signal même quand la fenêtre servie n'a pas bougé — sinon un « Rafraîchir » à période
      // identique rafraîchirait les barres mais pas la courbe.
      reloadKey: 0
    };
    this.fetchFunnel = this.fetchFunnel.bind(this);
    this.onRefresh = this.onRefresh.bind(this);
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.applyPeriod = this.applyPeriod.bind(this);
    this.handleSegmentChange = this.handleSegmentChange.bind(this);
    this.exportCSV = this.exportCSV.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchFunnel();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // Lecture seule : tout vient de l'agrégat v2 (B6_03), le front affiche, il n'agrège pas.
  fetchFunnel() {
    const { from, to, segment } = this.state;
    this.setState({ isLoading: true, error: null, errorCode: null });
    getFunnel(from, to, segment)
      .then((result) => {
        if (!this._isMounted) return;
        this.setState({ data: result, isLoading: false });
      })
      .catch((err) => {
        if (!this._isMounted) return;
        this.setState({
          error: (err && err.message) || 'Erreur de chargement',
          // Une session expirée n'est PAS un vide de données : elle a son propre affichage.
          errorCode: (err && err.code) || null,
          isLoading: false
        });
      });
  }

  onRefresh(e) {
    if (e && e.preventDefault) e.preventDefault();
    // `reloadKey` s'incrémente ICI et non dans `fetchFunnel` (revue 🟡-1) : « Rafraîchir » est
    // le seul chemin où la fenêtre servie ne bouge pas — la courbe a besoin d'un signal dédié.
    // Sur les autres chemins (montage, raccourci, segment), l'enfant refetche déjà via ses
    // props `servi.*` ; incrémenter dans `fetchFunnel` doublait chaque appel `/timeseries`
    // (l'enfant monte et fetche AVANT le componentDidMount du parent).
    this.setState((s) => ({ reloadKey: s.reloadKey + 1 }));
    this.fetchFunnel();
  }

  // Édition manuelle d'un champ date : on stocke, le rechargement attend le « Rafraîchir ».
  handlePeriodChange(from, to) {
    this.setState({ from, to });
  }

  // Raccourci de période (7j/30j/trimestre) : on stocke ET on recharge immédiatement.
  applyPeriod(from, to) {
    this.setState({ from, to }, this.fetchFunnel);
  }

  // Changement de segment : rechargement immédiat (le segment est un paramètre SERVEUR, il n'y
  // a rien à filtrer côté client). `null` = clic sur le bouton déjà actif → on ignore, MUI
  // désélectionnerait tout et l'écran n'aurait plus de segment.
  handleSegmentChange(e, value) {
    // ⚠ La garde de chargement vit ICI et non dans un `disabled` sur le groupe : MUI émet un
    // `console.error` par enfant désactivé sous un <Tooltip> (Tooltip.js — « you should not
    // put a disabled child inside a Tooltip »), soit 3 erreurs à chaque rendu. Ignorer le
    // clic donne le même résultat fonctionnel sans polluer la console.
    if (this.state.isLoading) return;
    if (!value || !SEGMENTS.includes(value)) return;
    if (value === this.state.segment) return;
    this.setState({ segment: value }, this.fetchFunnel);
  }

  exportCSV() {
    const { data, error } = this.state;
    const steps = data && Array.isArray(data.steps) ? data.steps : [];
    // ⛔ Jamais d'export sur un écran en erreur : `data` n'est pas purgé par le `.catch`, donc
    // il porte encore la réponse PRÉCÉDENTE. Sans cette garde, un échec de bascule de segment
    // produisait un fichier titré du segment demandé, rempli des lignes de l'ancien.
    if (!steps.length || error) return;
    const { from, to, segment } = servedLabels(data, this.state);
    downloadCSV(
      `funnel_${segment}_${from}_${to}.csv`,
      rowsToCSV(buildCsvRows(data, from, to, segment))
    );
  }

  // Un périmètre de la carte MRR (cohorte ou parc). Le titre dit LEQUEL : les deux
  // chiffres ne se comparent qu'à condition de savoir ce que chacun couvre (D17).
  renderMrrPerimetre(titre, perimetre) {
    const { etat, montants, mention } = describeMrr(perimetre);
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {titre}
        </Typography>
        {etat === 'calcule' ? (
          <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
            {montants.map((m) => (
              <Typography key={m} variant="h6">{m}</Typography>
            ))}
          </Stack>
        ) : (
          <Typography variant="h6" color="text.secondary">{LIBELLES_ETAT_MRR[etat]}</Typography>
        )}
        {mention && (
          <Typography variant="caption" color="text.secondary">{mention}</Typography>
        )}
      </Box>
    );
  }

  // Les deux paliers de rétention, SOUS la barre « Rétention » (D20). La barre porte la part
  // de toute la cohorte, comparable à ses voisines ; les paliers portent les taux sur base
  // mature, chacun avec son dénominateur explicite. Deux bases, jamais mélangées.
  renderRetentionTiers(retention, tierPromu) {
    const tiers = Array.isArray(retention) ? retention : [];
    if (!tiers.length) return null;
    return (
      <Box sx={{ mt: 1, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
        {tiers.map((entry) => {
          const t = describeTier(entry);
          return (
            <Stack
              key={t.tier || t.label}
              direction="row"
              spacing={1}
              alignItems="baseline"
              data-testid={`funnel-tier-${t.tier || 'inconnu'}`}
              data-measurable={String(t.measurable)}
              data-denominator={String(t.denominator)}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 40 }}>
                {t.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t.text}
                {t.measurable && t.rateText ? ` (${t.rateText})` : ''}
                {/* R6 — sans cette mention, le lecteur voit « 60 (24,4 %) » sur la barre puis
                    « 60 / 173 (34,7 %) » deux lignes plus bas : MÊME population, DEUX bases,
                    et rien ne le dit. La barre porte la part de toute la cohorte (D20), le
                    palier la part de la cohorte mature. */}
                {t.tier && t.tier === tierPromu ? ' — même population que la barre, rapportée à la seule cohorte mature' : ''}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    );
  }

  // Seconde lecture de la conversion, placée SOUS les deux étapes qu'elle nuance (décision
  // Mathias 2026-09-02). `conversions.current` n'est PAS rendu ici : ce sont exactement les
  // volumes des étapes « Souscription » et « Payant actif » ci-dessus — les afficher une
  // seconde fois ferait croire à deux mesures là où il n'y en a qu'une.
  renderInvoicedLine(conversions) {
    const inv = describeInvoiced((conversions || {}).invoiced);
    return (
      <Box
        sx={{ mt: 1, pl: 2, borderLeft: 2, borderColor: 'divider' }}
        data-testid="funnel-conversions-invoiced"
        data-sites={String(inv.sites)}
      >
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Facturé sur la période
          </Typography>
          {inv.absent ? (
            /* ⛔ Le bloc n'est pas servi (API antérieure au contrat v2) : ce n'est PAS
               « aucune vente ». Affirmer un zéro non mesuré est le défaut central que ce
               ticket corrige — il ne doit pas rentrer par la porte de derrière. */
            <Typography variant="caption" color="text.disabled">{NON_MESURE}</Typography>
          ) : inv.hasSale ? (
            <Typography variant="caption" color="text.secondary">
              {inv.sites} site{inv.sites > 1 ? 's' : ''}
              {inv.montants.length ? ` · ${inv.montants.join(' · ')}` : ''}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              aucune vente facturée sur la période
            </Typography>
          )}
          <Tooltip title="Reconstruit depuis les factures PAYÉES. Contrairement aux deux étapes ci-dessus, ce chiffre est stable : un client parti en disparaît pas — il a bien acheté. C'est un PLANCHER : une facture émise après le départ d'un client n'est jamais rattachée à son site. Montant facturé et encaissé, non net de remboursement.">
            <Chip size="small" variant="outlined" icon={<InfoOutlinedIcon />} label="plancher" />
          </Tooltip>
        </Stack>
      </Box>
    );
  }

  renderFunnelBars(data) {
    const nf = new Intl.NumberFormat('fr-FR');
    const steps = Array.isArray(data.steps) ? data.steps : [];
    const passages = computePassages(steps);
    const worst = worstPassage(steps, passages);

    return (
      <Stack spacing={1.5} data-testid="funnel-bars">
        {steps.map((s, i) => {
          const measurable = stepMeasurable(s);
          const volume = stepVolume(s);
          const rateText = formatRate(s.rate);
          const rateValue = measurable && rateText !== null ? Number(s.rate) : null;
          const passage = passages[i];
          const isWorst = i === worst.index;
          const reason = stepReason(s);
          const connected = stepConnected(s);
          return (
            <Box
              key={s.step}
              data-testid={`funnel-step-${s.step}`}
              data-measurable={String(measurable)}
            >
              {i > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mb: 0.5,
                    color: isWorst ? (worst.sameNature ? 'error.main' : 'warning.main') : 'text.secondary',
                    fontWeight: isWorst ? 700 : 400
                  }}
                >
                  ↓ {passage !== null ? `${formatRate(passage)} de passage` : 'passage n/a'}
                  {isWorst
                    ? worst.sameNature
                      ? ' · plus gros décrochage'
                      : ` · plus fort écart — mesures de natures différentes (${worst.from} → ${worst.to}), ce n’est pas une fuite`
                    : ''}
                </Typography>
              )}
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.25 }}>
                <Typography variant="subtitle2">
                  {STEP_LABELS[s.step] || s.step}
                  {/* `steps[].tier` est SERVI : la barre « Rétention » est le palier J+30
                      rapporté à la cohorte entière. Le taire laissait deux pourcentages
                      différents pour le même volume, à deux lignes d'écart. */}
                  {s.tier ? ` (${TIER_LABELS[s.tier] || s.tier})` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {volume === null ? (
                    <Typography component="span" variant="body2" color="text.disabled">
                      {NON_MESURE}
                      {reason ? ` (${reason})` : ''}
                    </Typography>
                  ) : (
                    <>
                      {nf.format(volume)}{' '}
                      <Typography component="span" variant="caption">
                        {rateText !== null ? `(${rateText})` : `(${NON_MESURE})`}
                      </Typography>
                      {/* Installations reliées à un site : l'écart installé → relié dit si le
                          plugin est posé puis abandonné avant la connexion. Borné à `installed`
                          (seule étape où l'API sert `connected`) pour que l'ancre reste un oracle
                          unique — piège « testid dans un rendu partagé », super-admin/CLAUDE.md. */}
                      {s.step === 'installed' && connected !== null && (
                        <Typography
                          component="span"
                          variant="caption"
                          data-testid="funnel-installed-connected"
                          data-connected={String(connected)}
                        >
                          {` · dont ${connectedLabel(connected)}`}
                        </Typography>
                      )}
                    </>
                  )}
                </Typography>
              </Stack>
              {rateValue === null ? (
                // ⛔ Pas de barre à zéro pour une étape non mesurée : une barre vide se lit
                // « zéro », et c'est exactement la confusion que D21 interdit. Un gabarit
                // en pointillés dit « il n'y a rien à dessiner ici », pas « c'est nul ».
                <Box
                  sx={{
                    height: 12,
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'divider'
                  }}
                />
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, rateValue * 100))}
                  color={isWorst && worst.sameNature ? 'error' : 'primary'}
                  sx={{ height: 12, borderRadius: 1 }}
                />
              )}
              {s.step === 'retention' && this.renderRetentionTiers(data.retention, s.tier)}
              {s.step === 'paying' && this.renderInvoicedLine(data.conversions)}
            </Box>
          );
        })}
      </Stack>
    );
  }

  renderTriggersCard(triggers) {
    const absent = triggers === null || triggers === undefined;
    const t = triggers || {};
    const breakdown = t.breakdown || {};
    const nf = new Intl.NumberFormat('fr-FR');
    // R3 — le périmètre et le libellé sont SERVIS pour être dits (contrat §9.9), pas codés en
    // dur : le jour où l'API bascule ces compteurs sur la cohorte, la carte doit suivre au
    // lieu de mentir. Repli sur la clé brute, comme partout ailleurs dans cet écran.
    const cle = t.label_key || 'legacy_ai_unlock';
    const perimetre = TRIGGER_SCOPE_LABELS[t.scope] || t.scope || 'périmètre non précisé';
    const principal = absent ? null : Number(breakdown[cle]);
    return (
      <Card variant="outlined" sx={{ height: '100%' }} data-testid="funnel-triggers">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Compteurs de parc</Typography>
            <Tooltip title={`Deux compteurs d'état, sur ${perimetre} et non sur la période affichée. Ce ne sont pas des motifs d'achat : rien n'est capturé au moment du passage payant.`}>
              <Chip size="small" color="default" variant="outlined" icon={<InfoOutlinedIcon />} label={perimetre} />
            </Tooltip>
          </Stack>
          {/* ⛔ Un bloc `triggers` absent de la réponse n'est pas « 0 site » : afficher un
              zéro en gros chiffre serait exactement le défaut que B5_01 vient de corriger. */}
          <Typography variant="h5" color={principal === null || !Number.isFinite(principal) ? 'text.disabled' : 'inherit'}>
            {principal === null || !Number.isFinite(principal) ? NON_MESURE : nf.format(principal)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {TRIGGER_LABELS[cle] || cle}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {/* `quota_exceeded` n'est PAS un réglage hérité : c'est un état de facturation
                vivant, et les sites qui le portent sont majoritairement gratuits. Il a donc
                sa propre note — le ranger en silence sous le libellé du déblocage IA
                répéterait le défaut que ce ticket corrige. */}
            <Tooltip title="Communautés actuellement au-delà de leur quota, gratuites comprises. Remis à zéro à la résiliation : ce compteur ne dit pas combien de communautés ont dépassé leur quota par le passé.">
              <Chip
                size="small"
                label={`${TRIGGER_LABELS.quota} : ${
                  absent || !Number.isFinite(Number(breakdown.quota)) ? NON_MESURE : nf.format(Number(breakdown.quota))
                }`}
              />
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  renderMrrCard(mrr) {
    const m = mrr || {};
    return (
      <Card variant="outlined" sx={{ height: '100%' }} data-testid="funnel-mrr">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1">MRR estimé</Typography>
            <Tooltip title="Estimé depuis le prix des plans en base, sans Stripe live. Les abonnements annuels sont ramenés au mois ; les devises ne sont jamais additionnées entre elles.">
              <Chip size="small" color="warning" variant="outlined" icon={<InfoOutlinedIcon />} label="estimation" />
            </Tooltip>
          </Stack>
          <Stack spacing={1.5}>
            {this.renderMrrPerimetre('Communautés créées sur la période', m.cohort)}
            {this.renderMrrPerimetre('Parc entier (repère)', m.platform)}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  renderDeactivationsCard(deactivations) {
    const d = describeDeactivations(deactivations);
    const nf = new Intl.NumberFormat('fr-FR');
    return (
      <Card
        variant="outlined"
        sx={{ height: '100%' }}
        data-testid="funnel-deactivations"
        data-denominator={String(d.denominator)}
      >
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            {/* L17 — le libellé temporel vit dans l'EN-TÊTE, donc dans TOUS les états de la
                carte (y compris « non mesuré » et « pas de dénominateur ») : ce compteur n'est
                pas borné à la période affichée, contrairement à la courbe juste au-dessus. */}
            <Typography variant="subtitle1">
              Désinstallations{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                — depuis l'ouverture du suivi
              </Typography>
            </Typography>
            <Tooltip title="Sites de la cohorte ayant signalé une désactivation du plugin, rapportés aux sites CAPABLES d'en remonter une (ceux qui ont émis au moins un signal du plugin). Compteur cumulé depuis l'ouverture du canal plugin, NON borné à la période affichée — la courbe « Séries quotidiennes », elle, compte sur la période : son total peut être plus bas, c'est attendu. ⛔ Ce n'est pas rapporté aux activations : le trafic widget et le canal plugin sont deux populations différentes, et les diviser l'une par l'autre donne un taux faux.">
              <Chip size="small" color="default" variant="outlined" icon={<InfoOutlinedIcon />} label="canal plugin" />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="h5" color={d.absent ? 'text.disabled' : 'inherit'}>
              {d.absent ? NON_MESURE : nf.format(d.volume)}
            </Typography>
            {d.rateText && (
              <Typography variant="body2" color="text.secondary">({d.rateText})</Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">{d.text}</Typography>
        </CardContent>
      </Card>
    );
  }

  renderSegmentSelector() {
    const { segment } = this.state;
    return (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={segment}
        onChange={this.handleSegmentChange}
        aria-label="Segment d'origine"
      >
        {SEGMENT_OPTIONS.map((o) => (
          <Tooltip key={o.value} title={o.help}>
            <ToggleButton value={o.value} data-testid={`funnel-segment-${o.value}`}>
              {o.label}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    );
  }

  render() {
    const { from, to, segment, data, isLoading, error, errorCode, reloadKey } = this.state;
    const steps = data && Array.isArray(data.steps) ? data.steps : [];
    const cohorte = cohortSize(data);
    // ⛔ Le bandeau décrit les CHIFFRES AFFICHÉS, pas les champs de saisie : il lit donc
    // l'écho servi. Sans cela, éditer « Du » sans rafraîchir renommait la période d'un
    // tableau qu'elle ne couvre pas (même faute que l'export — cf. `servedLabels`).
    const servi = servedLabels(data, this.state);
    const segmentOption =
      SEGMENT_OPTIONS.find((o) => o.value === servi.segment) || SEGMENT_OPTIONS[0];

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Funnel d'acquisition</Typography>
          <PeriodSelector
            from={from}
            to={to}
            isLoading={isLoading}
            onChange={this.handlePeriodChange}
            onApplyPeriod={this.applyPeriod}
            onRefresh={this.onRefresh}
            actions={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {this.renderSegmentSelector()}
                <Button
                  variant="outlined"
                  size="small"
                  data-testid="funnel-export"
                  startIcon={<FileDownloadOutlinedIcon />}
                  disabled={isLoading || !!error || !data || !steps.length}
                  onClick={this.exportCSV}
                >
                  Exporter CSV
                </Button>
              </Stack>
            }
          />
        </Stack>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Une session expirée n'est PAS un vide de données (décision L8, 2026-09-02) :
            `ensureAuthenticated` répond HTTP 200 + {gcCode:410} et `withAuth` ne revalide
            qu'au montage — un onglet laissé ouvert puis « Rafraîchir » tombait sur
            « Aucune donnée sur la période ». */}
        {!isLoading && error && errorCode === SESSION_EXPIRED && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="funnel-session-expired">
            {/* ⛔ Ne rien promettre en dessous : le bloc de données est gaté sur `!error`,
                il n'y a donc JAMAIS de chiffres sous cette alerte. Et surtout ne pas se
                mettre à rendre les chiffres périmés pour rendre la phrase vraie. */}
            Votre session a expiré. Recharge la page pour te reconnecter.
          </Alert>
        )}

        {!isLoading && error && errorCode !== SESSION_EXPIRED && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="funnel-error">
            Impossible de charger le funnel : {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {!isLoading && !error && data && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle1">Étapes du funnel</Typography>
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  data-testid="funnel-bandeau"
                  data-segment={servi.segment}
                >
                  ({servi.from} → {servi.to}) · segment « {segmentOption.label} » ·{' '}
                  {new Intl.NumberFormat('fr-FR').format(cohorte)} communauté
                  {cohorte > 1 ? 's' : ''} dans la cohorte
                </Typography>
              </Stack>
              {/* R4/D24 — la césure doit être lisible sans survol : au tactile, une infobulle
                  n'existe pas, et le contresens « hors WordPress » se lit, lui, en continu. */}
              {servi.segment === 'unknown' && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 2 }}
                  data-testid="funnel-segment-note"
                >
                  {SEGMENT_UNKNOWN_NOTE}
                </Typography>
              )}
              {cohorte > 0 ? (
                <>
                  {this.renderFunnelBars(data)}
                  {/* Note de comparabilité des ÂGES. ⚠ Elle ne dit PAS « les sites récents
                      plombent le taux » : mesuré (M4), c'est l'inverse — ils le tirent vers
                      le haut. Ce qu'il faut dire est que la base n'est pas homogène. */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 2 }}
                    data-testid="funnel-note-ages"
                  >
                    Ces taux mélangent des communautés d'âges différents : une communauté créée
                    hier ne peut pas encore avoir trente jours de recul. Les paliers sous la
                    barre « Rétention » comparent, eux, des cohortes de même âge — chacun avec
                    son dénominateur.
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 3, textAlign: 'center' }}
                  data-testid="funnel-empty"
                >
                  Aucune communauté créée sur cette période
                  {servi.segment === 'all' ? '' : ` pour le segment « ${segmentOption.label} »`}.
                </Typography>
              )}
            </Paper>
          )}

          {/* Séries quotidiennes (B8_02) — monté HORS du gate `!isLoading && !error && data` :
              le composant fait son propre appel et ne doit être démonté ni par un « Rafraîchir »
              ni par une erreur de l'agrégat (exigence du ticket, prouvée par `data-mount-count`).
              Fenêtre/segment SERVIS (`servedLabels`), jamais la saisie en cours — un « Du » édité
              sans rafraîchir ne déclenche rien (objectif 3) ; `reloadKey` fait suivre chaque
              fetch de l'agrégat, fenêtre identique comprise. */}
          <FunnelTimeseries
            from={servi.from}
            to={servi.to}
            segment={servi.segment}
            reloadKey={reloadKey}
          />

          {!isLoading && !error && data && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {this.renderTriggersCard(data.triggers)}
              </Grid>
              <Grid item xs={12} md={4}>
                {this.renderMrrCard(data.mrr)}
              </Grid>
              <Grid item xs={12} md={4}>
                {this.renderDeactivationsCard(data.deactivations)}
              </Grid>
            </Grid>
          )}
        </Stack>
      </Box>
    );
  }
}

export { SEGMENT_OPTIONS };
export default Funnel;
